import { useDebounceFn } from '@vueuse/core'
import { useNuxtApp, useRoute } from 'nuxt/app'
import { watch } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { getConfig } from '@common/core-layer/config'
import { log } from '@common/core-layer/services/logger.service'
import { isCoursesContextPath } from '@common/core-layer/utils/route-path-segments'
import { assetsDrmRegion } from '@common/http-layer/utils/regional-host'

import type { Pinia } from 'pinia'

export default defineNuxtPlugin(async () => {
  const route = useRoute()
  const pinia = useNuxtApp().$pinia as Pinia
  const { store: authStore } = useAuth(pinia)

  let currentSource: 'courses' | 'communities' = 'communities'

  /**
   * Determine DRM source based on route path
   * - Top-level `/courses` app routes and group `/learning/courses/` paths → courses
   * - Otherwise → communities (including group slugs like `courses-MD`)
   */
  const getDrmSource = (path: string): 'courses' | 'communities' => {
    if (isCoursesContextPath(path)) {
      return 'courses'
    }
    return 'communities'
  }

  const drmHeadersForRegion = (region?: 'us' | 'au') => ({
    source: 'PORTAL_USER',
    // SDK resolveRegion also reads custom headers when the pin is unset.
    ...(region === 'au' ? { 'x-dr-region': 'au' as const } : {}),
  })

  type DrmSetupModule = typeof import('@ghl-revex/assets-drm-client/setup') & {
    setRegion?: (region: 'us' | 'au') => void
  }

  /**
   * Pin SDK region from the latest auth/host signal.
   * Call after init and on every token change — init can race ahead of token hydration.
   * Skip when the region is still unknown so a default US pin cannot lock the client.
   */
  const syncDrmRegion = async (token?: string | null) => {
    const region = assetsDrmRegion(token)
    if (!region) return
    try {
      const drmSetup = (await import('@ghl-revex/assets-drm-client/setup')) as DrmSetupModule
      drmSetup.setRegion?.(region)
    } catch (error) {
      log.error('Failed to update assets drm region', { error, source: currentSource })
    }
  }

  /**
   * Initialize or reinitialize Assets DRM client with the given source
   */
  const initializeDrmClient = async (source: 'courses' | 'communities') => {
    const config = getConfig()
    try {
      const drmSetup = (await import('@ghl-revex/assets-drm-client/setup')) as DrmSetupModule

      // Resolve region after the dynamic import so a token that hydrated during
      // the await is not missed (common on AU clientclub cold load).
      const region = assetsDrmRegion(authStore.token)
      const drmInitOptions = {
        // in local i.e development, point assets drm to staging else use config mode
        env: config?.mode === 'development' ? 'staging' : config?.mode || 'staging',
        source,
        ...(region ? { region } : {}),
        getAuthTokenCallback: getAuthTokenCallback(),
        headers: drmHeadersForRegion(region),
      } as Parameters<typeof drmSetup.initAssetsDrmClient>[0]
      drmSetup.initAssetsDrmClient(drmInitOptions)
      // Re-pin only a known region. An unknown region stays unset so the SDK can infer it.
      if (region) drmSetup.setRegion?.(region)
      currentSource = source
    } catch (error) {
      log.error('Failed to initialize assets drm client', { error, source })
    }
  }

  // Initialize with current route source
  const initialSource = getDrmSource(route.path)
  await initializeDrmClient(initialSource)

  // Debounced route watcher to handle source changes
  const handleRouteChange = useDebounceFn(async (newPath: string) => {
    const newSource = getDrmSource(newPath)
    if (newSource !== currentSource) {
      await initializeDrmClient(newSource)
    }
  }, 300) // 300ms debounce

  // Watch for route changes
  watch(() => route.path, handleRouteChange)

  // immediate: token is often already on the store after async init; without this,
  // AU region from JWT never re-pins the SDK and assets-license stays on US host.
  watch(
    () => authStore.token,
    token => {
      void syncDrmRegion(token)
    },
    { immediate: true }
  )
})

function currentAuthToken(): string | undefined {
  try {
    const pinia = useNuxtApp().$pinia as Pinia
    const { store } = useAuth(pinia)
    return store.token || undefined
  } catch {
    return undefined
  }
}

function getAuthTokenCallback() {
  return async () => currentAuthToken() || ''
}
